import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function UserEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userFromState = location.state?.user;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "agent",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [updating, setUpdating] = useState(false);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  useEffect(() => {
    // Use user from state if available
    if (userFromState) {
      setFormData({
        name: userFromState.name,
        email: userFromState.email,
        role: userFromState.role,
      });
      setLoading(false);
      return;
    }

    // Fetch user from API if not passed via state
    const fetchUser = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/admin_panel/v1/users/show.php?id=${id}`,
          { method: "GET" }
        );

        if (!response.ok) throw new Error(`API returned status: ${response.status}`);
        const result = await response.json();

        if (result.success) {
          setFormData({
            name: result.data.name,
            email: result.data.email,
            role: result.data.role,
          });
        } else {
          throw new Error(result.message || "Failed to fetch user data.");
        }
      } catch (err) {
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id, API_BASE_URL, userFromState]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdateError(null);
    setUpdating(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/admin_panel/v1/users/update.php`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id, ...formData }), // id in body
        }
      );

      if (!response.ok) throw new Error(`API returned status: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        toast.success("User updated successfully!");
        navigate("/users");
      } else {
        throw new Error(result.message || "Failed to update user.");
      }
    } catch (err) {
      setUpdateError(err.message || "An error occurred while updating the user.");
      toast.error(err.message || "An error occurred while updating the user.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading)
    return <p style={{ color: "#fff", textAlign: "center" }}>Loading user data...</p>;

  if (error)
    return (
      <div
        style={{
          padding: "20px",
          margin: "20px auto",
          background: "rgba(255, 255, 255, 0.1)",
          border: "1px solid red",
          borderRadius: "10px",
          maxWidth: "800px",
          color: "#fff",
          textAlign: "center",
        }}
      >
        <h3 style={{ color: "#F87171" }}>Error Fetching User</h3>
        <p>{error}</p>
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
      }}
    >
      <ToastContainer />
      <div
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          padding: "30px",
          borderRadius: "15px",
          maxWidth: "600px",
          margin: "0 auto",
          boxShadow: "0 6px 15px rgba(0, 0, 0, 0.2)",
        }}
      >
        <h2 style={{ textAlign: "center", marginBottom: "25px" }}>Edit User</h2>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Name:</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "none",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontWeight: "500",
              }}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "none",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontWeight: "500",
              }}
            />
          </div>

          {/* Role */}
          <div style={{ marginBottom: "15px" }}>
            <label style={{ display: "block", marginBottom: "5px" }}>Role:</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "none",
                borderRadius: "8px",
                background: "rgba(255,255,255,0.9)",
                color: "#1E293B",
                fontWeight: "500",
              }}
            >
              <option value="agent">Agent</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {updateError && (
            <p style={{ color: "#DC2626" }}>
              <strong>Error:</strong> {updateError}
            </p>
          )}

          <button
            type="submit"
            disabled={updating}
            style={{
              padding: "10px 20px",
              background: "#F97316",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontWeight: "500",
              cursor: updating ? "not-allowed" : "pointer",
            }}
          >
            {updating ? "Updating..." : "Update User"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default UserEditPage;
